import { Box, Link, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LottoShell from "../components/lotto/LottoShell";
import { useCustomColors } from "../hooks/useCustomColors";

const LAST_UPDATED = "2022-06-21";

const TABS = [
  { id: "terms", path: "/terms", labelKey: "legal_terms_tab" },
  { id: "privacy", path: "/privacy", labelKey: "legal_privacy_tab" },
] as const;

const TERMS_SECTIONS = [
  "acceptance",
  "eligibility",
  "account",
  "wallet",
  "betting",
  "fair_play",
  "responsible",
  "suspension",
  "liability",
  "changes",
  "law",
  "contact",
];

const PRIVACY_SECTIONS = [
  "collect",
  "use",
  "sharing",
  "cookies",
  "retention",
  "security",
  "rights",
  "children",
  "contact",
];

const LegalPage = () => {
  const colors = useCustomColors();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  const activeTab = pathname.startsWith("/privacy") ? "privacy" : "terms";
  const sections = activeTab === "privacy" ? PRIVACY_SECTIONS : TERMS_SECTIONS;

  const updatedOn = new Date(LAST_UPDATED).toLocaleDateString(
    i18n.language.startsWith("zh") ? "zh-CN" : "en-GB",
    { day: "2-digit", month: "long", year: "numeric" }
  );

  return (
    <LottoShell>
      <Box sx={{ maxWidth: "900px", mx: "auto" }}>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "24px", md: "32px" },
            fontWeight: 800,
            color: colors.text.primary,
          }}
        >
          {t("legal_title")}
        </Typography>
        <Typography
          sx={{ mt: "6px", fontSize: "13px", color: colors.text.secondary }}
        >
          {t("legal_updated", { date: updatedOn })}
        </Typography>

        <Box
          sx={{
            mt: "20px",
            p: "4px",
            display: "inline-flex",
            gap: "4px",
            borderRadius: "22px",
            backgroundColor: colors.background.rail,
            border: `1px solid ${colors.card.border}`,
          }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <Box
                key={tab.id}
                component="button"
                type="button"
                onClick={() => navigate(tab.path)}
                sx={{
                  height: "35px",
                  px: "22px",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: "18px",
                  fontSize: "13px",
                  fontWeight: 700,
                  color: isActive ? "#FFFFFF" : colors.text.secondary,
                  background: isActive ? colors.gradient.brand : "none",
                  "&:hover": { color: colors.isDark ? "#FFFFFF" : colors.text.primary },
                }}
              >
                {t(tab.labelKey)}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            mt: "20px",
            p: { xs: "20px", md: "32px" },
            borderRadius: "18px",
            backgroundColor: colors.card.background,
            border: `1px solid ${colors.card.border}`,
            boxShadow: colors.card.shadow,
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: 1.9,
              color: colors.text.secondary,
            }}
          >
            {t(`legal_${activeTab}_intro`)}
          </Typography>

          <Stack sx={{ mt: "28px", gap: "28px" }}>
            {sections.map((section, index) => (
              <Box key={section} component="section">
                <Typography
                  component="h2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 800,
                    color: colors.text.primary,
                  }}
                >
                  {index + 1}. {t(`legal_${activeTab}_${section}_title`)}
                </Typography>
                {t(`legal_${activeTab}_${section}_body`)
                  .split("\n")
                  .map((paragraph) => (
                    <Typography
                      key={paragraph}
                      sx={{
                        mt: "10px",
                        fontSize: "14px",
                        lineHeight: 1.9,
                        color: colors.text.secondary,
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
              </Box>
            ))}
          </Stack>

          <Typography
            sx={{
              mt: "32px",
              pt: "20px",
              borderTop: `1px solid ${colors.card.border}`,
              fontSize: "12.5px",
              lineHeight: 1.8,
              color: colors.text.disabled,
            }}
          >
            {t("legal_footer_note")}{" "}
            <Link
              href={`mailto:${t("legal_contact_email")}`}
              underline="hover"
              sx={{ color: colors.accent.gold }}
            >
              {t("legal_contact_email")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </LottoShell>
  );
};

export default LegalPage;
